import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { imprimirPdf } from "@/lib/download";
import { toast } from "sonner";
import { ScanLine, CheckCircle2, Printer, Truck } from "lucide-react";
import { Escaner } from "@/components/empaque/Escaner";
import { Felicidades, felMsg } from "@/components/Felicidades";

const hora = (r) => (r?.at ? new Date(r.at).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala", hour: "2-digit", minute: "2-digit" }) : "");

/** Carga del camión: un solo botón, cada QR se asigna a su pedido sin importar el orden. El avance se ve en la tarjeta de cada pedido. */
export const CargaCamion = ({ items = [], onChanged }) => {
  const [scan, setScan] = useState(false);
  const [ult, setUlt] = useState(null);
  const [busy, setBusy] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [fel, setFel] = useState(null);
  const leer = async (codigo) => {
    try {
      const { data } = await api.post("/domicilios/escanear-carga", { codigo });
      const a = data.avance;
      setUlt({ ok: true, txt: `${data.pedido}: ${a.cargados}/${a.total} cargados${a.total - a.cargados ? ` · faltan ${a.total - a.cargados}` : " · completo"} — ${data.unidad.producto}` });
      const r = data.ruta || { total: 0, cargados: 0 };
      const falta = r.total - r.cargados;
      setFel(falta <= 0
        ? { id: Date.now(), completo: true, titulo: "🎉 ¡FELICITACIONES!", texto: "CARGA COMPLETA" }
        : { id: Date.now(), completo: false, titulo: "🎉 ¡EXCELENTE!", texto: `${r.cargados} ${r.cargados === 1 ? "CARGADO" : "CARGADOS"} – ${falta === 1 ? "FALTA" : "FALTAN"} ${falta}` });
      if (navigator.vibrate) navigator.vibrate(120);
      onChanged?.();
    } catch (e) {
      if (navigator.vibrate) navigator.vibrate([80, 60, 80]);
      setUlt({ ok: false, txt: formatApiError(e) });
    }
  };
  return (
    <div className="mb-3" data-testid="carga-camion">
      <button onClick={() => { setUlt(null); setFel(null); setScan(true); }} className="w-full h-20 rounded-xl bg-black text-white text-2xl font-black uppercase flex items-center justify-center gap-3" data-testid="carga-escanear">
        <Truck className="w-8 h-8" /> Cargar productos
      </button>
      <button type="button" onClick={() => setAbierto(!abierto)} className="w-full mt-2 h-14 rounded-xl border-2 border-zinc-900 bg-white text-lg font-black uppercase flex items-center justify-center gap-2" data-testid="resumen-toggle">
        Resumen de productos {abierto ? "▲" : "▼"}
      </button>
      {abierto && <ResumenCarga items={items} busy={busy} onMarcar={async (o, u) => {
        setBusy(u.codigo);
        try { await api.post(`/domicilios/pedido/${o.id}/resumen-marca`, { codigo: u.codigo, marcado: !u.marcado }); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
      }} />}
      {scan && (
        <Escaner titulo="CARGA DEL CAMIÓN" placeholder="Código de la etiqueta" continuo onCode={leer} onClose={() => { setScan(false); onChanged?.(); }}>
          {ult && <div className={`p-3 text-base font-bold ${ult.ok ? "bg-emerald-600" : "bg-red-700"}`} data-testid="carga-resultado">{ult.txt}</div>}
          <Felicidades msg={fel} />
        </Escaner>
      )}
    </div>
  );
};

/** Resumen de productos de la ruta: marcar/desmarcar «Cargado» solo cambia el color (no toca contadores ni pedidos). */
const ResumenCarga = ({ items, busy, onMarcar }) => {
  const filas = items.flatMap((o) => (o.items || []).map((it, i) => ({ o, it, key: `${o.id}-${i}`, us: (o.unidades || []).filter((u) => u.item === i + 1) }))).filter((f) => f.us.length);
  if (!filas.length) return <div className="mt-2 text-center text-zinc-500">Sin productos en la ruta.</div>;
  const total = filas.reduce((n, f) => n + f.us.length, 0);
  const hechos = filas.reduce((n, f) => n + f.us.filter((u) => u.marcado).length, 0);
  return (
    <div className="mt-2 rounded-xl border-2 border-zinc-900 overflow-hidden" data-testid="resumen-carga">
      <div className={`px-4 py-3 flex justify-between items-center text-lg font-black ${hechos === total ? "bg-emerald-100" : "bg-zinc-100"}`}>
        <span>Productos de la ruta</span><span data-testid="resumen-carga-total">{hechos}/{total} marcados</span>
      </div>
      <div className="divide-y-2 divide-zinc-100">
        {filas.map(({ o, it, key, us }) => (
          <div key={key} className="p-3 space-y-2">
            <div className="text-xl font-black leading-tight">{it.cantidad}× {it.product_name}</div>
            {(it.estilo || it.tamano || it.color) && <div className="text-base font-semibold text-zinc-600">{[it.estilo, it.tamano, it.color].filter(Boolean).join(" · ")}</div>}
            <div className="grid grid-cols-1 gap-2">
              {us.map((u) => (
                <button key={u.codigo} disabled={busy === u.codigo} onClick={() => onMarcar(o, u)}
                  className={`h-14 rounded-lg border-2 text-lg font-black uppercase flex items-center justify-center gap-2 disabled:opacity-50 ${u.marcado ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-zinc-400 text-black"}`} data-testid={`carga-btn-${u.codigo}`}>
                  {u.marcado && <CheckCircle2 className="w-6 h-6" />} Cargado · unidad {u.unidad}/{u.de}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Contador de carga dentro de la tarjeta del pedido (junto al número). */
export const CargaContador = ({ pedido }) => {
  const a = pedido.avance;
  if (!a?.total) return null;
  const ok = a.cargados === a.total;
  return <span className={`text-sm font-black px-2 py-1 rounded-full border-2 ${ok ? "bg-white border-zinc-300 text-zinc-800" : "bg-amber-300 border-amber-500 text-black"}`} data-testid={`carga-capsula-${pedido.numero}`}>{a.cargados}/{a.total} cargados{ok ? "" : ` · faltan ${a.total - a.cargados}`}</span>;
};

/** Unidades de un producto en la lista principal: verde cuando ya se escaneó su entrega. */
export const UnidadesItem = ({ pedido, idx }) => {
  const us = (pedido.unidades || []).filter((u) => u.item === idx + 1);
  if (!us.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {us.map((u) => <span key={u.codigo} className={`text-xs font-bold px-2 py-1 rounded ${u.entregado ? "bg-emerald-600 text-white" : u.cargado ? "bg-zinc-200 text-zinc-800" : "bg-amber-100 text-amber-900"}`} title={u.entregado ? `${u.entregado.by} ${hora(u.entregado)}` : ""} data-testid={`entrega-unidad-${u.item}-${u.unidad}`}>{u.entregado ? <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> : null}Unidad {u.unidad}/{u.de}{u.entregado ? " entregada" : u.cargado ? " cargada" : " sin cargar"}</span>)}
    </div>
  );
};

/** Control de entrega dentro del pedido: avance + botón grande; las unidades se marcan en la lista principal. */
export const EntregaProductos = ({ pedido, onChanged, onCompleto }) => {
  const [scan, setScan] = useState(false);
  const [ult, setUlt] = useState(null);
  const [fel, setFel] = useState(null);
  const us = pedido.unidades || [];
  const av = pedido.avance || { total: 0, entregados: 0 };
  const leer = async (codigo) => {
    try {
      const { data } = await api.post(`/domicilios/pedido/${pedido.id}/escanear-entrega`, { codigo });
      setUlt({ ok: true, txt: `Entregado: ${data.unidad.producto} (${data.avance.entregados}/${data.avance.total})` });
      setFel(felMsg(data.avance.total - data.avance.entregados, "entregar", "producto", "productos", "ENTREGA COMPLETA"));
      if (navigator.vibrate) navigator.vibrate(120);
      onChanged?.();
      if (data.completo) setTimeout(() => { setScan(false); onCompleto?.(); }, 2200);
    } catch (e) {
      if (navigator.vibrate) navigator.vibrate([80, 60, 80]);
      setUlt({ ok: false, txt: formatApiError(e) });
    }
  };
  if (!us.length) return null;
  return (
    <div className="mt-3 pt-3 border-t-2 border-zinc-100" data-testid="entrega-productos">
      <div className="flex items-center justify-between text-base font-black mb-2">
        <span>Entrega de productos</span>
        <span className={av.entregados === av.total ? "text-emerald-700" : "text-amber-700"} data-testid="entrega-avance">{av.entregados}/{av.total} entregados · faltan {av.total - av.entregados}</span>
      </div>
      <button onClick={() => { setUlt(null); setFel(null); setScan(true); }} className="w-full h-32 rounded-xl bg-emerald-600 active:bg-emerald-700 text-white text-2xl font-black uppercase flex items-center justify-center gap-3 leading-tight px-4" data-testid="entrega-escanear">
        <ScanLine className="w-10 h-10 shrink-0" /> Escanear entrega de productos
      </button>
      {scan && (
        <Escaner titulo={`ENTREGA ${pedido.numero}`} placeholder="Código de la etiqueta" continuo onCode={leer} onClose={() => { setScan(false); onChanged?.(); }}>
          {ult && <div className={`p-3 text-base font-bold ${ult.ok ? "bg-emerald-600" : "bg-red-700"}`} data-testid="entrega-resultado">{ult.txt}</div>}
          <Felicidades msg={fel} />
        </Escaner>
      )}
    </div>
  );
};

/** Imprimir guía de despacho / etiquetas QR (5×2.5 cm) / ambas. Siempre los mismos QR. */
export const ImprimirEtiquetas = ({ orderId, className = "", compact = false }) => {
  const [busy, setBusy] = useState(false);
  const run = async (modo) => {
    setBusy(true);
    try { await imprimirPdf(`/orders/${orderId}/etiquetas.pdf?modo=${modo}`); } catch (e) { toast.error(formatApiError(e) || "No se pudo imprimir"); } finally { setBusy(false); }
  };
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="imprimir-etiquetas-grupo">
      {[["guia", "Guía de despacho"], ["etiquetas", "Etiquetas QR"], ["ambas", "Guía + etiquetas"]].map(([m, l]) => (
        <button key={m} type="button" disabled={busy} onClick={() => run(m)} className={`${compact ? "h-8 px-2 text-[10px] rounded border" : "h-14 px-4 text-sm rounded-lg border-2"} border-zinc-400 bg-white font-black uppercase flex items-center gap-1 disabled:opacity-50`} data-testid={`imprimir-${m}`}>
          <Printer className={compact ? "w-3 h-3" : "w-5 h-5"} /> {l}
        </button>
      ))}
    </div>
  );
};
